import React, { useCallback, useEffect, useState, useContext } from 'react';
import styled from 'styled-components';

import AccountBackground from '../components/backgrounds/AccountBackground';
import ProfileSection from '../components/sections/ProfileSection';
import MainButton from '../components/buttons/MainButton';
import MainButtonInvert from '../components/buttons/MainButtonInvert';
import { useAuth } from '../context/AuthContext';
import { WalletContext } from '../context/WalletContext';
import { useSuccess } from '../context/SuccessContext';
import { useError } from '../context/ErrorContext';
import { useLoading } from '../context/LoadingContext';
import { useReward } from '../context/RewardContext';
import { signOut } from 'aws-amplify/auth';

import { incentivizeUpload, incentivizeUploadStripe } from '../apis/IncentivizeUpload';
import { createStripeAccount, checkStripeAccountStatus, regenerateStripeAccountLink } from '../apis/StripeApi';
import { getUserDataUploads } from '../apis/DataUpload';
import { getDataPoolInfo } from '../apis/DataPoolInfo';
import { getTransactions } from '../apis/DataTransactions';

import FeatureCard from '../components/cards/FeatureCard';
import RowTransactionsCard from '../components/cards/RowTransactionsCard';
import { faLinkedin, faTiktok, faYoutube, faXTwitter, faFacebook, faSpotify } from '@fortawesome/free-brands-svg-icons';
import { faDigitalTachograph } from '@fortawesome/free-solid-svg-icons/faDigitalTachograph';
import PaymentSelectionModal from '../components/modals/PaymentSelectionModal';

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    width: auto;
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  border-bottom: 1px solid #ccc;
  width: 100%;
`;

const Tab = styled.div`
  padding: 10px 20px;
  cursor: pointer;
  color: ${(props) => (props.isActive ? 'white' : '#999')};
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0px;
    bottom: -1px;
    height: 2px;
    background-color: ${(props) => (props.isActive ? 'white' : 'transparent')};
    transition: background-color 0.3s ease;
  }

  &:hover {
    color: white;
  }
`;

const idConverter = (id) => {
    switch (id) {
        case 'linkedin':
            return faLinkedin;
        case 'tiktok':
            return faTiktok;
        case 'youtube':
            return faYoutube;
        case 'x':
            return faXTwitter;
        case 'facebook':
            return faFacebook;
        case 'spotify':
            return faSpotify;
        default:
            return faDigitalTachograph;
    }
}

const Account = () => {
    const [activeTab, setActiveTab] = useState('Rewards');
    const [rewards, setRewards] = useState([]);
    const [claimedRewards, setClaimedRewards] = useState([]);
    const [transactions, setTransactions] = useState([]);

    const { openLoadingModal, closeLoadingModal } = useLoading();
    const { openRewardModal } = useReward();
    const { openSuccessModal } = useSuccess();
    const { openErrorModal } = useError();

    const { user, checkUserAuth, userAttributes } = useAuth();
    const { walletAddress, connectWallet, disconnectWallet } = useContext(WalletContext);

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [selectedRewardId, setSelectedRewardId] = useState(null); 

    const fetchRewards = useCallback(async () => {
        if (!user?.username) {
            return;
        }

        try {
            const userRewards = await getUserDataUploads(user?.username);
            // filer out the rewards that have been claimed
            const claimedRewardsData = userRewards.filter(reward => reward.rewardClaimed === true);
            setClaimedRewards(claimedRewardsData);
            // filter out the rewards that have not been claimed
            const rewardsData = userRewards.filter(reward => reward.rewardClaimed === false);

            // Add dataPoolName to the array of rewards objects
            const dataPoolNameOfRewards = await Promise.all(rewardsData.map(async (reward) => {
                const dataPoolInfo = await getDataPoolInfo(reward.dataPoolId);
                return { ...reward, dataPoolName: dataPoolInfo?.dataPoolName };
            }));

            setRewards(dataPoolNameOfRewards);
        } catch (err) {
            console.error('Error fetching rewards:', err);
        } finally {
            // setLoading(false);
            console.log('Rewards fetched');
        }
    }, [user]);

    const fetchTransactions = useCallback(async () => {
        if (!user?.username) {
            return;
        }

        try {
            const transactions = await getTransactions(walletAddress);
            console.log('Transactions:', transactions);

            setTransactions(transactions);

        } catch (err) {
            console.error('Error fetching transactions:', err);
        } finally {
            // setLoading(false);
            console.log('Transactions fetched');
        }
    }, [user, walletAddress]);

    const downloadFile = async (presignedUrl) => {
        try {
            // Fetch the file
            const response = await fetch(presignedUrl, { mode: 'cors' });

            // Check if the response is okay
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Verify content-length
            const contentLength = response.headers.get('content-length');
            console.log('Content-Length from headers:', contentLength);

            // Convert to Blob
            const blob = await response.blob();
            console.log('Blob size:', blob.size);

            // Check Blob size matches content-length
            if (contentLength && parseInt(contentLength, 10) !== blob.size) {
                throw new Error('Blob size does not match content-length');
            }

            // Create Object URL and trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'data.zip'; // Provide a file name
            a.click();

            // Cleanup the Object URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleTab = (tab) => {
        setActiveTab(tab);
        if (tab === 'Transactions') {
            fetchTransactions();
        }
    }

    const handleSignOut = async () => {
        try {
            await signOut();
            checkUserAuth();
            await disconnectWallet();
            window.location.href = '/';
        } catch (error) {
            console.error('Error signing out:', error);
        }
    }

    const handleConfirmPayment = async () => {
        if (!selectedRewardId) return;
        try {
          if (selectedPayment === 'stripe') {
            await handleClaimRewardStripe(selectedRewardId);
          } else if (selectedPayment === 'cgdx') {
            await handleClaimReward(selectedRewardId);
          }
          // all done, close modal
          setIsPaymentModalOpen(false);
        } catch (err) {
          // handle or display error
          console.error(err);
        }
      };

    
    const handleClaimReward = async (dataPoolId) => {
        try {
            // If user is not connected to a wallet, prompt them to connect
            if (!walletAddress) {
                openErrorModal({
                    errorHeader: 'Error',
                    errorMessage: 'Please connect your wallet to claim rewards'
                });
                return;
            }

            openLoadingModal({
                loadingHeader: 'Claiming Reward...',
                loadingMessage: 'Please wait while we credit the CGDX tokens to your wallet'
            })

            await incentivizeUpload(user?.username, dataPoolId, walletAddress);

            closeLoadingModal();

            fetchRewards();
            openSuccessModal({
                successHeader: 'Success',
                successMessage: 'Reward claimed successfully, please check your wallet for the CGDX tokens'
            });
        } catch (error) {
            console.error(error);
            closeLoadingModal();
            openErrorModal({
                errorHeader: 'Error',
                errorMessage: 'Error claiming reward'
            });
        }
    }

    const handleClaimRewardStripe = async (dataPoolId) => {
        try {
            openLoadingModal({
                loadingHeader: 'Claiming Reward...',
                loadingMessage: 'Please wait while we transfer the funds to your Stripe account'
            })

            const txData = await incentivizeUploadStripe(user?.username, dataPoolId, walletAddress);

            closeLoadingModal();

            fetchRewards();
            openSuccessModal({
                successHeader: 'Success',
                successMessage: `Reward claimed successfully, please check your Stripe account for the funds. Transaction ID: ${txData.transferId}`
            });
        } catch (error) {
            console.error(error);
            closeLoadingModal();
            openErrorModal({
                errorHeader: 'Error',
                errorMessage: 'Error claiming reward'
            });
        }
    }

    const handleStripeInit = async () => {
        try {
            if (userAttributes?.['custom:stripe_account_id']) {
                // Fetch current Stripe account status
                const data = await checkStripeAccountStatus(userAttributes['custom:stripe_account_id']);

                if (!data) {
                    console.error("Failed to fetch Stripe account status.");
                    return;
                }
                if (!data.charges_enabled) {
                    alert("Your Stripe account is incomplete. Redirecting you to finish setup.");
                } else {
                    alert("Stripe is already set up!");
                    return;
                }
            }
    
            // Continue with onboarding
            const data = await createStripeAccount();
            if (data) {
                window.open(data.url, '_blank').focus();
            } else {
                console.error('Error creating stripe account');
            }
        } catch (error) {
            console.error(error);
        }
    };
    
    const handleReconnectStripe = async () => {
        try {
            const stripeAccountId = userAttributes?.['custom:stripe_account_id'];
            if (!stripeAccountId) {
                console.error("No Stripe account ID found.");
                return;
            }
            // Step 1: Check current Stripe account status
            if (!userAttributes?.['custom:stripe_account_id']) {
                console.error("No Stripe account ID found.");
                return;
            }
    
            const data = await checkStripeAccountStatus(stripeAccountId);
            if (!data) {
                console.error("Failed to fetch Stripe account status.");
                return;
            }

            // Step 2: If Stripe account is incomplete, regenerate onboarding link
            if (!data.charges_enabled) {
                console.log("Stripe account not fully onboarded. Regenerating onboarding link...");
    
                const regenerateData = await regenerateStripeAccountLink(stripeAccountId);
                if (regenerateData.url) {
                    window.open(regenerateData.url, '_blank').focus();
                } else {
                    console.error("Error regenerating Stripe link:", regenerateData);
                }
            } else {
                alert("Your Stripe account is already fully set up!");
            }
        } catch (error) {
            console.error("Error handling Stripe reconnection:", error);
        }
    };
    

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    }

    useEffect(() => {
        fetchRewards();
    }, [fetchRewards]);

    return (
        <>
            {user &&
                <AccountBackground>
                    <ProfileSection>
                        <div>
                            <h2>Account</h2>
                            <h5>{user?.signInDetails?.loginId}</h5>
                        </div>
                        <ButtonContainer className='button-container'>
                            <MainButton
                                header='Sign Out'
                                subheader='Click to sign out'
                                onClick={handleSignOut}
                            />
                            <MainButton
                                header={walletAddress ? 'Wallet Connected' : 'Connect Wallet'}
                                subheader={walletAddress ? 'Click to disconnect' : 'Connect your wallet'}
                                onClick={walletAddress ? disconnectWallet : connectWallet}
                                active={walletAddress}
                            />
                            <MainButton
                                header={userAttributes?.['custom:stripe_account_id'] ? 'Stripe Connected' : 'Connect Stripe'}
                                subheader={userAttributes?.['custom:stripe_account_id'] ? 'Click to re-onboard if needed' : 'Click to connect to Stripe'}
                                onClick={userAttributes?.['custom:stripe_account_id'] ? handleReconnectStripe : handleStripeInit}
                                active={userAttributes?.['custom:stripe_account_id']}
                            />
                        </ButtonContainer>
                        <TabsContainer>
                            <Tab isActive={activeTab === 'Rewards'} onClick={() => setActiveTab('Rewards')}>
                                Rewards
                            </Tab>
                            <Tab isActive={activeTab === 'Claimed'} onClick={() => setActiveTab('Claimed')}>
                                Claimed
                            </Tab>
                            <Tab isActive={activeTab === 'Transactions'} onClick={() => handleTab('Transactions')}>
                                Transactions
                            </Tab>
                        </TabsContainer>
                        {activeTab === 'Rewards' && rewards.length > 0 &&
                            <div className='rewards-container'>
                                {rewards.map((reward, index) => (
                                    <FeatureCard
                                        key={index}
                                        icon={idConverter(reward.dataPoolId)}
                                        title={reward.dataPoolName}
                                        description={<>Data Quality Score: {reward.dataQualityScore}, <br></br>  Click to claim the rewards</>}
                                        // onClick={() => openRewardModal({
                                        //     stripe: async () => handleClaimRewardStripe(reward.dataPoolId),
                                        //     cgdx: async () => handleClaimReward(reward.dataPoolId)
                                        // })}
                                        onClick={() => {
                                            setSelectedRewardId(reward.dataPoolId);
                                            setIsPaymentModalOpen(true);
                                            setSelectedPayment('stripe'); //default payment set to stripe
                                        }}
                                        direction='left'
                                    />
                                ))}
                            </div>
                        }
                        <PaymentSelectionModal
                            isOpen={isPaymentModalOpen}
                            onClose={() => setIsPaymentModalOpen(false)}
                            selectedPayment={selectedPayment}
                            onSelectPayment={methodName => setSelectedPayment(methodName)}
                            title="Select Payment Method"
                            subheader="Choose how you want to receive your reward:"
                            confirmButton={
                                <MainButtonInvert
                                header="Confirm"
                                subheader="Get your reward"
                                onClick={handleConfirmPayment}
                                />
                            }
                        />

                        {activeTab === 'Rewards' && rewards.length === 0 &&
                            <div className='no-rewards-container'>
                                <h4>No rewards available</h4>
                                <p>Contribute data to DataPools to earn rewards</p>
                                <MainButton header='Explore DataPools' subheader='Contribute data to earn rewards' onClick={() => window.location.href = '/DataPool'} />
                            </div>
                        }
                        {activeTab === 'Claimed' && claimedRewards.length > 0 &&
                            <div className='rewards-container'>
                                {claimedRewards.map((reward, index) => (
                                    <FeatureCard
                                        key={index}
                                        icon={idConverter(reward.dataPoolId)}
                                        title={reward.dataPoolId}
                                        description={
                                            <>
                                                Data Quality Score: {reward.dataQualityScore}, claimed
                                                <br />
                                                Upload Hash <span>(Click to Copy)</span>:
                                                <br />
                                                {reward.uploadHash}
                                            </>
                                        }
                                        onClick={() => copyToClipboard(reward.uploadHash)}
                                        direction='left'
                                    />
                                ))}
                            </div>
                        }
                        {activeTab === 'Claimed' && claimedRewards.length === 0 &&
                            <div className='no-rewards-container'>
                                <div>
                                    <h4>No rewards claimed</h4>
                                    <p>Earn rewards by contributing data to DataPools</p>
                                </div>
                                <MainButton header='Explore DataPools' subheader='Contribute data to earn rewards' onClick={() => window.location.href = '/DataPool'} />
                            </div>
                        }
                        {activeTab === 'Transactions' && transactions.length === 0 &&
                            <div className='no-rewards-container'>
                                <h4>No transactions</h4>
                                <p>
                                    Connect your wallet to view transactions
                                </p>
                                <MainButton header='Refresh' subheader='Click to refresh transactions' onClick={fetchTransactions} />
                            </div>
                        }
                        {activeTab === 'Transactions' && transactions.length > 0 &&
                            <div className='transactions-container'>
                                <h4>Click on the transaction to download the data</h4>
                                {transactions.map((transaction, index) => (
                                    <RowTransactionsCard
                                        key={index}
                                        txHash={transaction.transactionHash}
                                        time={transaction.createdAt}
                                        txStatus={transaction.status}
                                        description={transaction.averageDataQuality}
                                        cost={transaction.spentAmount}
                                        boughtHashes={transaction.uploadHashes}
                                        onClick={() => downloadFile(transaction.presignedUrl)}
                                    />
                                ))}
                            </div>
                        }
                    </ProfileSection>
                </AccountBackground>
            }
        </>
    )
}

export default Account;
